/*
 * Purpose: LinkedIn OAuth callback — exchange code and return to compose
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import {
  exchangeLinkedInCode,
  parseOAuthState,
  persistLinkedInToken,
  startLinkedInOAuth,
} from '@/services/connectors/linkedinOAuth';
import { ContextualError } from '@/components/errors/ContextualError';
import { VaultUnlockInline } from '@/components/vault/VaultUnlockInline';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { toast } from 'sonner';

export default function LinkedInOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [returnPath, setReturnPath] = useState('/compose');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const oauthError = searchParams.get('error_description') || searchParams.get('error');

    if (oauthError) {
      setError(oauthError);
      return;
    }
    if (!code) {
      setError('Missing authorization code from LinkedIn.');
      return;
    }

    const parsed = parseOAuthState(state);
    setReturnPath(parsed.returnPath);
    if (!parsed.valid) {
      setError('OAuth state mismatch — session expired or opened in another tab.');
      return;
    }

    void (async () => {
      try {
        const token = await exchangeLinkedInCode(code);
        persistLinkedInToken(token.access_token);
        toast.success('LinkedIn connected');
        navigate(parsed.returnPath, { replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'LinkedIn connection failed');
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md space-y-3">
        <CardHeader>
          <CardTitle className="text-lg">LinkedIn sign-in</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {error ? (
            <>
              <VaultUnlockInline compact />
              <ContextualError
                message={error}
                action={{
                  label: 'Try Connect again',
                  onClick: () => startLinkedInOAuth(returnPath),
                }}
                secondaryAction={{ label: 'Back to Compose', href: REMEDIATION.compose }}
              />
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => navigate(REMEDIATION.setup)}>
                  Open Setup
                </Button>
                <Button size="sm" variant="ghost" onClick={() => navigate(returnPath)}>
                  Back to flow
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Completing connection…
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
